import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  GraduationCap, Users, BarChart3, Building, User, ArrowRight, Shield,
  CheckCircle, AlertCircle, Loader, LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Demo Role Configs ─────────────────────────────────────────────────────────
const ROLES = [
  {
    id: 'student_malik',
    label: 'Student (On Track)',
    subtitle: 'See what Malik sees',
    description: 'Apex High Grade 12, career plan built, 92 WBL hours, AWS credential. CDP 35% in progress. The compliance form auto-builds from his exploration.',
    icon: GraduationCap,
    color: 'cyan',
    credential: { email: 'highschool@test.com', password: 'Ali2020' },
    redirectTo: '/app/my-school-plan',
    badge: 'On track',
    facts: ['CTE: Information Technology', 'Career: Software Engineer', 'WBL: 92h logged', 'Assessment: IRT The Builder'],
  },
  {
    id: 'student_jordan',
    label: 'Student (Needs Attention)',
    subtitle: 'See what Jordan sees',
    description: 'Apex High Grade 11, no career goal set, only 1 of 9 CDP sections complete, zero WBL hours. Exactly what a counselor flags as urgent.',
    icon: GraduationCap,
    color: 'rose',
    credential: { email: 'jordan.williams@apex.wcpss.demo', password: 'Demo2026!' },
    redirectTo: '/app/my-school-plan',
    badge: 'Needs attention',
    facts: ['No career goal set', 'CDP: 10% (1/9 sections)', 'WBL: 0 hours', 'Counselor flagged'],
  },
  {
    id: 'parent_denise',
    label: 'Parent (On Track Child)',
    subtitle: "See what Denise sees",
    description: "Malik's mother, a read-only window into her son's career plan, WBL progress, and NC compliance status. She never touches the form.",
    icon: User,
    color: 'emerald',
    credential: { email: 'parent.johnson@wcpss.demo', password: 'Demo2026!' },
    redirectTo: '/app/parent/child-cdp',
    badge: 'Parent portal',
    facts: ["Malik's CDP: 35% + growing", 'WBL: 92h employer verified', 'Career direction: visible', 'FERPA read-only'],
  },
  {
    id: 'parent_marcus',
    label: 'Parent (Attention Needed)',
    subtitle: "See what Marcus sees",
    description: "Jordan's father, sees his son's incomplete CDP, no career goal, and zero WBL hours. Clear signal the family needs to act.",
    icon: User,
    color: 'orange',
    credential: { email: 'parent.williams@wcpss.demo', password: 'Demo2026!' },
    redirectTo: '/app/parent/child-cdp',
    badge: 'Parent portal',
    facts: ['Jordan CDP: 10% incomplete', 'No career direction yet', 'WBL: 0 hours', 'Action needed'],
  },
  {
    id: 'counselor',
    label: 'Counselor View',
    subtitle: 'See what C. King sees',
    description: 'Charles King at Apex High, real-time priority queue showing Malik (in progress) and Jordan (needs attention) side-by-side with 76 other students.',
    icon: Users,
    color: 'blue',
    credential: { email: 'c.king@apex.wcpss.demo', password: 'Demo2026!' },
    redirectTo: '/app/pathways/counselor',
    badge: '78-student command center',
    facts: ['Malik: Software Engineer, CDP 35%', 'Jordan: Flagged, no direction', 'Priority queue live', 'One-click student brief'],
  },
  {
    id: 'superintendent',
    label: 'District Leader View',
    subtitle: "Dr. Taylor's district view",
    description: 'Wake County Superintendent Dr. Robert P. Taylor, 37 schools, 5,112 students, CTO briefing card with 100% data privacy score, drill-down by school.',
    icon: BarChart3,
    color: 'amber',
    credential: { email: 'superintendent@futurepath.demo', password: 'Demo2026!' },
    redirectTo: '/app/institution/superintendent',
    badge: 'District intelligence',
    facts: ['37 schools · 5,112 students', 'Data Privacy: 100%', 'CTO audit scorecard', 'School drill-down + counselors'],
  },
];

const COLOR_MAP = {
  cyan: {
    gradient: 'from-cyan-500/20 to-cyan-500/5',
    border: 'border-cyan-500/40',
    hoverBorder: 'hover:border-cyan-400/70',
    text: 'text-cyan-400',
    badge: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
    btn: 'bg-cyan-600 hover:bg-cyan-500',
    icon: 'text-cyan-400',
    dot: 'bg-cyan-400',
  },
  rose: {
    gradient: 'from-rose-500/20 to-rose-500/5',
    border: 'border-rose-500/40',
    hoverBorder: 'hover:border-rose-400/70',
    text: 'text-rose-400',
    badge: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
    btn: 'bg-rose-600 hover:bg-rose-500',
    icon: 'text-rose-400',
    dot: 'bg-rose-400',
  },
  orange: {
    gradient: 'from-orange-500/20 to-orange-500/5',
    border: 'border-orange-500/40',
    hoverBorder: 'hover:border-orange-400/70',
    text: 'text-orange-400',
    badge: 'bg-orange-500/10 text-orange-300 border-orange-500/20',
    btn: 'bg-orange-600 hover:bg-orange-500',
    icon: 'text-orange-400',
    dot: 'bg-orange-400',
  },
  emerald: {
    gradient: 'from-emerald-500/20 to-emerald-500/5',
    border: 'border-emerald-500/40',
    hoverBorder: 'hover:border-emerald-400/70',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    btn: 'bg-emerald-600 hover:bg-emerald-500',
    icon: 'text-emerald-400',
    dot: 'bg-emerald-400',
  },
  blue: {
    gradient: 'from-blue-500/20 to-blue-500/5',
    border: 'border-blue-500/40',
    hoverBorder: 'hover:border-blue-400/70',
    text: 'text-blue-400',
    badge: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    btn: 'bg-blue-600 hover:bg-blue-500',
    icon: 'text-blue-400',
    dot: 'bg-blue-400',
  },
  violet: {
    gradient: 'from-violet-500/20 to-violet-500/5',
    border: 'border-violet-500/40',
    hoverBorder: 'hover:border-violet-400/70',
    text: 'text-violet-400',
    badge: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
    btn: 'bg-violet-600 hover:bg-violet-500',
    icon: 'text-violet-400',
    dot: 'bg-violet-400',
  },
  amber: {
    gradient: 'from-amber-500/20 to-amber-500/5',
    border: 'border-amber-500/40',
    hoverBorder: 'hover:border-amber-400/70',
    text: 'text-amber-400',
    badge: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    btn: 'bg-amber-600 hover:bg-amber-500',
    icon: 'text-amber-400',
    dot: 'bg-amber-400',
  },
};

function RoleCard({ role, onEnter, loading, success, error }) {
  const c = COLOR_MAP[role.color];
  const Icon = role.icon;
  const isLoading = loading === role.id;
  const isSuccess = success === role.id;
  const isError = error === role.id;

  return (
    <div
      className={`relative p-5 rounded-2xl border-2 bg-gradient-to-br ${c.gradient} ${c.border} ${c.hoverBorder} transition-all duration-200 cursor-pointer group`}
      onClick={() => !isLoading && !isSuccess && onEnter(role)}
      data-testid={`demo-role-${role.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl bg-slate-800/60 border ${c.border}`}>
            <Icon className={`w-5 h-5 ${c.icon}`} />
          </div>
          <div>
            <h3 className="font-bold text-white text-base">{role.label}</h3>
            <p className={`text-xs ${c.text}`}>{role.subtitle}</p>
          </div>
        </div>
        {isSuccess && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
        {isError && <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
      </div>

      {/* Description */}
      <p className="text-slate-300 text-sm mb-3 leading-relaxed">{role.description}</p>

      {/* Badge */}
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${c.badge} mb-3`}>
        <Shield className="w-3 h-3" />
        {role.badge}
      </div>

      {/* Facts */}
      <div className="grid grid-cols-2 gap-1 mb-4">
        {role.facts.map(fact => (
          <div key={fact} className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
            <span className="text-xs text-slate-300">{fact}</span>
          </div>
        ))}
      </div>

      {/* CTA button */}
      <button
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm text-white transition-all ${c.btn} ${isLoading ? 'opacity-75' : ''}`}
        disabled={isLoading || isSuccess}
      >
        {isLoading ? (
          <><Loader className="w-4 h-4 animate-spin" /> Signing in…</>
        ) : isSuccess ? (
          <><CheckCircle className="w-4 h-4" /> Redirecting…</>
        ) : (
          <>Enter as {role.label.split(' ')[0]} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
        )}
      </button>

      {isError && (
        <p className="text-xs text-red-400 mt-2 text-center">Login failed. Check that demo accounts are seeded.</p>
      )}
    </div>
  );
}

export default function WakeCountyDemoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Clear any leftover institutional flags from previous sessions on every page load
  useEffect(() => {
    localStorage.removeItem('institutional_shell');
    localStorage.removeItem('futurepath_token');
  }, []);

  const handleEnter = async (role) => {
    setLoading(role.id);
    setError(null);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: role.credential.email,
        password: role.credential.password,
      });
      const { access_token, user } = res.data;
      if (!access_token) throw new Error('No token returned');

      // Store auth token exactly like AuthContext does
      localStorage.setItem('futurepath_token', access_token);
      // Clear any previous institutional restrictions so the full app is visible
      localStorage.removeItem('institutional_shell');

      setSuccess(role.id);
      setLoading(null);

      setTimeout(() => {
        // Force a page reload so AuthContext picks up the new token
        window.location.href = role.redirectTo;
      }, 600);
    } catch (err) {
      setLoading(null);
      setError(role.id);
      console.error('Demo login error:', err.response?.data || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1729] text-white">
      {/* Top bar */}
      <div className="border-b border-slate-800/60 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/fp-logo.png" alt="FuturePath AI" className="h-7 w-7 rounded-md" />
            <span className="font-outfit font-bold text-white text-sm tracking-tight">
              <span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span>
            </span>
            <span className="hidden sm:block text-slate-300 text-sm mx-2">|</span>
            <span className="hidden sm:block text-slate-300 text-sm">Wake County Demo</span>
          </div>
          <a href="/login" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-300 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Exit demo
          </a>
        </div>
      </div>

      {/* Hero section */}
      <div className="max-w-5xl mx-auto px-4 pt-12 pb-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-4">
            <BarChart3 className="w-3.5 h-3.5" />
            Wake County Public School System
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            One platform.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Every perspective.</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            This is a live demonstration of Agentic FuturePath AI across all five stakeholder roles.
            Click any card to enter that view instantly, no passwords needed.
          </p>
        </div>

        {/* District stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {[
            { label: 'Schools', value: '37', color: 'text-cyan-400' },
            { label: 'Students', value: '5,112', color: 'text-blue-400' },
            { label: 'Counselors', value: '64', color: 'text-violet-400' },
            { label: 'District avg readiness', value: '61/100', color: 'text-amber-400' },
          ].map(stat => (
            <div key={stat.label} className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/40 text-center">
              <p className={`font-outfit text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-slate-300 text-xs mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Role cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLES.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onEnter={handleEnter}
              loading={loading}
              success={success}
              error={error}
            />
          ))}
        </div>

        {/* Demo story */}
        <div className="mt-10 p-6 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <h3 className="font-semibold text-white text-base mb-3">Suggested demo story (30 min)</h3>
          <div className="space-y-2">
            {[
              { step: '1', text: 'District Leader, Wake County overview, 37 schools side-by-side', highlight: 'Southeast Raleigh (34) vs Apex (74) = the story in one number' },
              { step: '2', text: 'Counselor, Ms. Chen\'s Apex High command center, priority queue', highlight: '80+ students, real-time, who needs attention today' },
              { step: '3', text: 'Student, Malik\'s FuturePath Plan (67% complete, he never filled a form)', highlight: 'CDP auto-built: assessment → career → courses → 92 WBL hours' },
              { step: '4', text: 'Parent, Denise\'s read-only view of Malik\'s NC CDP status', highlight: '"She sees exactly what the state requires, he owns it"' },
            ].map(item => (
              <div key={item.step} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-cyan-400 text-xs font-bold">{item.step}</span>
                </div>
                <div>
                  <p className="text-slate-300 text-sm">{item.text}</p>
                  <p className="text-cyan-400/70 text-xs mt-0.5 italic">{item.highlight}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-slate-300 text-xs mt-6">
          All data is seeded demo data. Re-seed anytime: <code className="bg-slate-800 px-1 rounded">python seed_wake_county.py</code>
        </p>
      </div>
    </div>
  );
}
